import { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail, getToken } from "../../api";
import { todayISO } from "./dates";

// The three fixed prompts, in the order they are easiest to answer standing in
// a corridor. Nothing here asks for an opinion — opinions are unusable.
const PROMPTS = [
  { key: "unexpected", label: "What happened that you didn't expect?",
    hint: "The thing you would tell someone in the car." },
  { key: "client_said", label: "What did the client say afterwards?",
    hint: "Their words, as close as you remember them." },
  { key: "went_wrong", label: "What went wrong, or nearly did?",
    hint: "Smooth days are not stored. Problems are." },
];

// A function, not a constant: the date has to be read when the form is
// (re)opened. `todayISO` is the calendar's own local-date helper — a plain
// `new Date().toISOString()` here pre-fills TOMORROW for anyone west of UTC
// filling this in after about 5pm, which is precisely when it gets filled in.
// And freezing the value at module load would pre-fill yesterday in a tab
// left open across midnight.
const empty = () => ({
  athlete_name: "", event: "", date: todayISO(),
  unexpected: "", client_said: "", went_wrong: "", notes: "",
});

export default function CaptureForm({ onDone, hidden = false }) {
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);   // the created item
  const [brokenPhotos, setBrokenPhotos] = useState(() => new Set());
  // Synchronous re-entrancy guard, same reasoning as ItemDrawer's `inFlight`:
  // `busy` (state) drives the disabled affordance but a state update is not
  // synchronous, so a second tap on a phone can land before React re-renders
  // the button as disabled. This ref is checked and set before the first
  // `await` — the create call and every photo upload inside `submit` all run
  // under this one guard, so a double tap cannot create a duplicate item or
  // double-attach a photo.
  const inFlight = useRef(false);

  // The Capture tab stays mounted (MarketingView hides it with the `hidden`
  // attribute rather than unmounting it) so a half-typed draft survives a
  // trip to the Queue tab and back. The confirmation screen is the one
  // piece of state that should NOT survive that trip: it already exists in
  // the Queue server-side, so leaving it up would show Ward stale, no
  // longer actionable data when he comes back — and worse, would look like
  // an unsent draft. Dismissing it the moment the tab is hidden means a
  // return to Capture always lands on a clean, ready-to-fill form.
  useEffect(() => {
    if (hidden) setDone(null);
  }, [hidden]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/marketing/captures", form);
      // The capture itself is already saved at this point. A photo that
      // fails to attach (too large, wrong extension) should not make Ward
      // think the whole capture was lost — it wasn't.
      let photoIds = [];
      // Every failure, not just the last one — three photos failing and Ward
      // being told about one is a silent loss of two.
      const photoFailures = [];
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const { data: photoData } = await api.post(
            `/marketing/captures/${data.id}/photo`, fd);
          photoIds = photoData.photo_ids;
        } catch (photoErr) {
          photoFailures.push({
            name: f.name,
            reason: formatApiErrorDetail(photoErr?.response?.data?.detail),
          });
        }
      }
      setBrokenPhotos(new Set());
      setDone({ ...data, photo_ids: photoIds, photo_failures: photoFailures });
      setForm(empty());
      setFiles([]);
      await onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mk-capture" data-testid="mk-capture-done" hidden={hidden}>
        <div className="mk-capture-done">
          <div className="mk-capture-donehead">In the queue.</div>
          <p>“{done.title}” is an idea now. It does not need anything else from you.</p>

          {done.photo_ids?.length > 0 && (
            <div className="mk-capture-photos">
              {done.photo_ids.map((p) => (
                brokenPhotos.has(p) ? (
                  <div key={p} className="mk-capture-photo-broken">photo unavailable</div>
                ) : (
                  <img key={p} className="mk-capture-photo" alt="captured"
                    // GET /api/files takes the cookie OR ?token=, never a Bearer
                    // header — see ItemDrawer's mk-photo-link for the same call.
                    src={`${API}/files?path=${encodeURIComponent(p)}&token=${encodeURIComponent(getToken() || "")}`}
                    onError={() => setBrokenPhotos((prev) => new Set(prev).add(p))} />
                )
              ))}
            </div>
          )}
          {done.photo_failures?.length > 0 && (
            <div className="mk-capture-photo-warn" data-testid="mk-capture-photo-warn">
              <div>
                Saved without {done.photo_failures.length}{" "}
                {done.photo_failures.length === 1 ? "photo" : "photos"}:
              </div>
              <ul className="mk-capture-photo-warnlist">
                {done.photo_failures.map((f, i) => (
                  <li key={`${f.name}-${i}`}>{f.name} — {f.reason}</li>
                ))}
              </ul>
            </div>
          )}

          <button type="button" className="mk-btn" onClick={() => setDone(null)}>
            Capture another
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="mk-capture" onSubmit={submit} data-testid="mk-capture" hidden={hidden}>
      <p className="mk-capture-intro">
        Straight after the event, before you get in the car. Ninety seconds.
        Answer whichever of these you can — one is enough.
      </p>

      <label className="mk-label" htmlFor="mk-capture-athlete">Athlete</label>
      <input id="mk-capture-athlete" className="mk-input" value={form.athlete_name}
        onChange={set("athlete_name")} autoComplete="off" data-testid="mk-capture-athlete" />

      <label className="mk-label" htmlFor="mk-capture-event">Event or client</label>
      <input id="mk-capture-event" className="mk-input" value={form.event}
        onChange={set("event")} autoComplete="off" />

      <label className="mk-label" htmlFor="mk-capture-date">Date</label>
      <input id="mk-capture-date" className="mk-input" type="date" value={form.date}
        onChange={set("date")} />

      {PROMPTS.map((p) => (
        <div key={p.key}>
          <label className="mk-label" htmlFor={`mk-capture-${p.key}`}>{p.label}</label>
          <div className="mk-hint">{p.hint}</div>
          <textarea id={`mk-capture-${p.key}`} className="mk-textarea" rows={4}
            value={form[p.key]} onChange={set(p.key)} data-testid={`mk-capture-${p.key}`} />
        </div>
      ))}

      <label className="mk-label" htmlFor="mk-capture-notes">Anything else</label>
      <textarea id="mk-capture-notes" className="mk-textarea" rows={3} value={form.notes}
        onChange={set("notes")} />

      <label className="mk-label" htmlFor="mk-capture-photos-input">Photos</label>
      <input id="mk-capture-photos-input" className="mk-input" type="file" accept="image/*"
        multiple capture="environment"
        onChange={(e) => setFiles(Array.from(e.target.files || []))}
        data-testid="mk-capture-photos" />
      {files.length > 0 && (
        <ul className="mk-capture-filelist">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <span>{f.name}</span>
              <button type="button" className="mk-btn-quiet" onClick={() => removeFile(i)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="mk-error" data-testid="mk-capture-error">{error}</div>}

      <button type="submit" className="mk-btn mk-capture-submit" disabled={busy}
        data-testid="mk-capture-submit">
        {busy ? "Sending…" : "Send it"}
      </button>
    </form>
  );
}
